import { Suspense, useState, useMemo, lazy } from "react";
import "./App.css";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import ScrollToTop from "./components/scrollTop/ScrollTop";
import { routes } from "./routes";
import Loader from "./components/loader";
import Cookies from "universal-cookie";
import RouteTracker from "./components/RouteTracker/RouteTracker";
import { useGeoBlock } from "./hooks/useGeoBlock";
import { getFloatingBarContent } from "./data/floating-bar-content";

// Lazy load non-critical UI components
const FloatingButtons = lazy(() => import("./components/floating-buttons"));
const ClutchIcon = lazy(() => import("./components/clutch-icon"));
const AiAutomationBar = lazy(() =>
  import("./components/ai-automation-bar/AiAutomationBar")
);

const cookies = new Cookies();

// Paths where floating elements should be hidden
const HIDDEN_FLOATING_PATHS = new Set(["/contact-us"]);
// The floating offer bar (AiAutomationBar) is shown site-wide with per-page
// copy; these routes opt out entirely.
const HIDDEN_BAR_PATHS = new Set(["/contact-us", "/lead-magnet", "/solutions/auraclinic"]);

function App() {
  const [isAccepted, setIsAccepted] = useState(() => cookies.get("cookiesAccepted"));
  const location = useLocation();
  const currentPath = location.pathname;

  const { loading, isBlocked } = useGeoBlock();

  // The admin panel + login render their own isolated chrome — no public
  // floating widgets there.
  const isAdminArea =
    currentPath === "/login" || currentPath.startsWith("/admin");

  // Memoize visibility checks
  const showFloatingElements = useMemo(
    () => !HIDDEN_FLOATING_PATHS.has(currentPath) && !isAdminArea,
    [currentPath, isAdminArea]
  );

  const showFloatingBar = useMemo(
    () => !HIDDEN_BAR_PATHS.has(currentPath) && !isAdminArea,
    [currentPath, isAdminArea]
  );

  // Show blocked message for restricted countries
  // Note: We no longer block on loading to improve initial render performance
  // The geo check happens in the background and will block if needed after check completes
  if (isBlocked) {
    return (
      <div className="geo-blocked-container">
        <div className="geo-blocked-content">
          <h1>Access Restricted</h1>
          <p>This service is currently not available in your region</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <ScrollToTop />

      {/* Lazy-loaded floating UI elements */}
      <Suspense fallback={null}>
        {showFloatingElements && (
          <>
            <ClutchIcon isAccepted={isAccepted} setIsAccepted={setIsAccepted} />
            <FloatingButtons />
          </>
        )}
        {showFloatingBar && (
          // `key` remounts the bar on route change so its open/auto-close
          // state resets and the new page's pitch animates in.
          <AiAutomationBar key={currentPath} {...getFloatingBarContent(currentPath)} />
        )}
      </Suspense>

      <RouteTracker />

      {/* Main routes with Suspense for code-split pages */}
      <Suspense fallback={<Loader />}>
        <Routes>
          {routes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
          <Route path="/us" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
